import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { updateSpreadsheetCells } from '../utils/helpers';
import { useCompany } from '../../../contexts/CompanyContext';

// Query key factory
const spreadsheetKeys = {
  all: ['spreadsheets'],
  byType: (type, year) => ['spreadsheets', type, year],
  detail: (type, year, userId) => ['spreadsheets', type, year, userId],
};

// Fetch spreadsheet data
const fetchSpreadsheetData = async ({ queryKey }) => {
  const [, type, year, userId] = queryKey;
  
  console.log('[fetchSpreadsheetData] Making API call:', {
    type,
    year,
    userId,
    timestamp: new Date().toISOString()
  });
  
  const response = await fetch(
    `https://plansync-backend-234288438538.us-central1.run.app/api/spreadsheet/${userId}/${type}/${year}`
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  console.log('[fetchSpreadsheetData] API call completed:', {
    type,
    dataLength: result?.data?.length || 0,
    timestamp: new Date().toISOString()
  });
  return result;
};

// Custom hook for spreadsheet data
export const useSpreadsheetQuery = (currentSheetType, year) => {
  const userId = localStorage.getItem('userid');
  const queryClient = useQueryClient();
  const { company, isCustomerView } = useCompany();
  
  // Debug logging
  console.log('[useSpreadsheetQuery] Hook called with:', {
    currentSheetType,
    year,
    userId,
    timestamp: new Date().toISOString()
  });

  // Query for fetching data
  const {
    data: queryData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
    queryFn: fetchSpreadsheetData,
    enabled: !!userId && !!currentSheetType && !!year,
    staleTime: 30 * 60 * 1000, // Consider data fresh for 30 minutes (matching prefetch)
    cacheTime: 60 * 60 * 1000, // Keep in cache for 60 minutes (matching prefetch)
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
  });

  // Mutation for updating cells
  const updateCellsMutation = useMutation({
    mutationFn: async (cellUpdates) => {
      try {
        const result = await updateSpreadsheetCells(userId, currentSheetType, year, cellUpdates);
        return result;
      } catch (error) {
        console.error('Error in updateCells mutation:', error);
        throw error;
      }
    },
    onMutate: async (cellUpdates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId)
      );

      // Optimistically update the cache
      queryClient.setQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId),
        (old) => {
          if (!old || !old.data) return old;
          const newData = [...old.data];
          cellUpdates.forEach(({ unid, columnId, value }) => {
            const rowIndex = newData.findIndex(row => row.unid === unid);
            if (rowIndex !== -1) {
              newData[rowIndex] = {
                ...newData[rowIndex],
                [columnId]: value,
              };
            }
          });
          return { ...old, data: newData };
        }
      );

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, cellUpdates, context) => {
      console.error('Update cells error:', err);
      console.error('Failed cell updates:', cellUpdates);
      
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId),
        context.previousData
      );
      
      const errorMessage = err?.message || 'Failed to update cells';
      message.error(errorMessage);
    },
    onSuccess: (data) => {
      console.log('Update cells success:', data);
      // Don't show success message for every cell update as it can be annoying
      // message.success('Cell updated successfully');
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
      });
    },
  });

  // Mutation for undo
  const undoMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `https://plansync-backend-234288438538.us-central1.run.app/api/undo/${userId}?spreadsheet_name=${currentSheetType}&year=${year}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (result) => {
      if (result.data) {
        // If we get complete data, update the cache directly
        queryClient.setQueryData(
          spreadsheetKeys.detail(currentSheetType, year, userId),
          result.data
        );
      } else if (result.updated_rows) {
        // Otherwise update individual rows
        queryClient.setQueryData(
          spreadsheetKeys.detail(currentSheetType, year, userId),
          (old = { data: [], columns: {}, dropdown_options: {}, row_heights: {}, column_widths: {} }) => {
            const newData = [...(old.data || [])];
            const updatedUnids = new Set();

            // Update existing rows
            Object.entries(result.updated_rows).forEach(([unid, updatedRow]) => {
              const idx = newData.findIndex(row => row.unid === unid);
              if (idx !== -1) {
                newData[idx] = { ...newData[idx], ...updatedRow };
                updatedUnids.add(unid);
              }
            });

            // Add new rows
            Object.entries(result.updated_rows).forEach(([unid, updatedRow]) => {
              if (!updatedUnids.has(unid)) {
                newData.push(updatedRow);
              }
            });

            // Remove deleted rows
            if (result.deleted_unids) {
              const filteredData = newData.filter(row => !result.deleted_unids.includes(row.unid));
              return { ...old, data: filteredData };
            }

            return { ...old, data: newData };
          }
        );
      } else {
        // Invalidate to refetch
        queryClient.invalidateQueries({
          queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
        });
      }
      message.success(result.message || 'Undo successful');
    },
    onError: () => {
      message.info('Nothing to undo');
    },
  });

  // Mutation for redo
  const redoMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `https://plansync-backend-234288438538.us-central1.run.app/api/redo/${userId}?spreadsheet_name=${currentSheetType}&year=${year}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (result) => {
      if (result.data) {
        // If we get complete data, update the cache directly
        queryClient.setQueryData(
          spreadsheetKeys.detail(currentSheetType, year, userId),
          result.data
        );
      } else if (result.updated_rows) {
        // Otherwise update individual rows
        queryClient.setQueryData(
          spreadsheetKeys.detail(currentSheetType, year, userId),
          (old = { data: [], columns: {}, dropdown_options: {}, row_heights: {}, column_widths: {} }) => {
            const newData = [...(old.data || [])];
            const updatedUnids = new Set();

            // Update existing rows
            Object.entries(result.updated_rows).forEach(([unid, updatedRow]) => {
              const idx = newData.findIndex(row => row.unid === unid);
              if (idx !== -1) {
                newData[idx] = { ...newData[idx], ...updatedRow };
                updatedUnids.add(unid);
              }
            });

            // Add new rows
            Object.entries(result.updated_rows).forEach(([unid, updatedRow]) => {
              if (!updatedUnids.has(unid)) {
                newData.push(updatedRow);
              }
            });

            // Remove deleted rows
            if (result.deleted_unids) {
              const filteredData = newData.filter(row => !result.deleted_unids.includes(row.unid));
              return { ...old, data: filteredData };
            }

            return { ...old, data: newData };
          }
        );
      } else {
        // Invalidate to refetch
        queryClient.invalidateQueries({
          queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
        });
      }
      message.success(result.message || 'Redo successful');
    },
    onError: () => {
      message.info('Nothing to redo');
    },
  });

  // Mutation for deleting rows
  const deleteRowsMutation = useMutation({
    mutationFn: async (unids) => {
      const userId = localStorage.getItem('userid');
      // Import the deleteSpreadsheetRow function
      const { deleteSpreadsheetRow } = await import('../../../api/mastersheetApi');
      
      // Delete each row
      for (const unid of unids) {
        await deleteSpreadsheetRow(userId, currentSheetType, year, unid);
      }
      return unids;
    },
    onMutate: async (unids) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId)
      );

      // Optimistically remove the rows
      queryClient.setQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId),
        (old = { data: [], columns: {}, dropdown_options: {}, row_heights: {}, column_widths: {} }) => {
          const filteredData = (old.data || []).filter(row => !unids.includes(row.unid));
          return { ...old, data: filteredData };
        }
      );

      return { previousData };
    },
    onError: (err, unids, context) => {
      // Roll back on error
      queryClient.setQueryData(
        spreadsheetKeys.detail(currentSheetType, year, userId),
        context.previousData
      );
      message.error('Failed to delete rows');
    },
    onSuccess: (unids) => {
      message.success(`Successfully deleted ${unids.length} row(s)`);
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: spreadsheetKeys.detail(currentSheetType, year, userId),
      });
    },
  });

  // Filter data by company if user is a customer
  let data = queryData?.data || [];
  if (isCustomerView && company) {
    // Filter data to only show rows for the user's company
    data = data.filter(row => {
      // Check for different possible company field names
      return row['Company Name'] === company || 
             row['Company'] === company ||
             row['company'] === company ||
             row['company_name'] === company;
    });
  }
  
  const columns = queryData?.columns || {};
  const dropdownOptions = queryData?.dropdown_options || {};
  const rowHeights = queryData?.row_heights || {};
  const columnWidths = queryData?.column_widths || {};

  return {
    data,
    columns,
    dropdownOptions,
    rowHeights,
    columnWidths,
    isLoading,
    isError,
    error,
    refetch,
    updateCells: updateCellsMutation.mutate,
    undo: undoMutation.mutate,
    redo: redoMutation.mutate,
    deleteRows: deleteRowsMutation.mutate,
    isUpdating: updateCellsMutation.isLoading,
    isUndoing: undoMutation.isLoading,
    isRedoing: redoMutation.isLoading,
    isDeleting: deleteRowsMutation.isLoading,
  };
};